import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { REFERRAL_APPLIED_EVENT } from '../lib/referrals';

/**
 * ReferralToast — "30 days of Plus added. Thanks for joining on a friend's link."
 *
 * Card 3MWZ8Zs7jA7e: the friend redeems a referral link during sign-up, and
 * the 30 days land server-side in the same breath (the worker pays the friend
 * half at apply time). Nothing on screen would say so — the reward is a change
 * in entitlement, not a screen — so this toast is the one confirmation the
 * friend gets.
 *
 * The toast shows ONLY on the event applyPendingReferral() dispatches after a
 * successful POST, so it can never claim days that did not land. It reuses the
 * app's bottom-toast recipe (.pear-update-toast, app.css) via a portal to body —
 * the app root carries a fade-in transform, and a transformed ancestor is the
 * containing block for position: fixed (the FktjQ7CT4keo lesson).
 */
export function ReferralToast() {
  const [days, setDays] = useState<number | null>(null);
  /* The founder code (card NThh9EjYzupt) pays Plus to 2099-12-31, not a day
     count, so the toast names the gift instead of a number of days. */
  const [founder, setFounder] = useState(false);

  useEffect(() => {
    const onApplied = (e: Event) => {
      const detail = (e as CustomEvent<{ rewardDays?: number; founder?: boolean }>).detail;
      const n = Number(detail?.rewardDays);
      setFounder(Boolean(detail?.founder));
      setDays(Number.isFinite(n) && n > 0 ? n : 30);
    };
    window.addEventListener(REFERRAL_APPLIED_EVENT, onApplied);
    return () => window.removeEventListener(REFERRAL_APPLIED_EVENT, onApplied);
  }, []);

  if (days === null) return null;

  if (founder) {
    return createPortal(
      <div className="pear-update-toast" role="status" data-testid="referral-toast" data-referral-founder="true">
        <span className="pear-update-toast-text">
          Pear Plus is yours until 31 December 2099 — a gift from Jonathan, the founder.
        </span>
        <button
          type="button"
          className="pear-update-toast-dismiss"
          aria-label="Dismiss referral notice"
          onClick={() => setDays(null)}
        >
          ✕
        </button>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      className="pear-update-toast"
      role="status"
      data-testid="referral-toast"
      data-referral-days={days}
    >
      <span className="pear-update-toast-text">
        {days} days of Pear Plus added — thanks for joining on a friend&rsquo;s link.
      </span>
      <button
        type="button"
        className="pear-update-toast-dismiss"
        aria-label="Dismiss referral notice"
        onClick={() => setDays(null)}
      >
        ✕
      </button>
    </div>,
    document.body,
  );
}
